/**
 * Registro central de proveedores de pago — providers-as-features.
 * Cada provider está atado a un featureId del catálogo SaaS (lifecycle, entitlements,
 * overrides y governance se resuelven con la maquinaria existente; NO se hardcodea
 * "if provider === payway" disperso).
 */
import { SM_FEATURES, type FeatureLifecycle } from "@/lib/feature-catalog";

export type PaymentProviderId = "payway" | "mercadopago" | "modo" | "getnet" | "uala" | "nave" | "talo" | "sipago" | "dlocal";

export interface PaymentProviderDef {
  id: PaymentProviderId;
  /** Feature del catálogo que gobierna el lifecycle/entitlement del provider. */
  featureId: string;
  label: string;
  /** Capacidades reales soportadas por el adapter de este provider. */
  capabilities: {
    cards: boolean;
    qr: boolean;
    links: boolean;
    recurring: boolean;
    refunds: boolean;
  };
  /** Campos de credenciales tenant-owned que requiere el provider. */
  credentialFields: { key: string; label: string; secret?: boolean }[];
}

export const PAYMENT_PROVIDERS: PaymentProviderDef[] = [
  {
    id: "payway",
    featureId: "payment_payway",
    label: "Payway",
    capabilities: { cards: true, qr: false, links: true, recurring: true, refunds: true },
    credentialFields: [
      { key: "public_key", label: "Public API Key" },
      { key: "private_key", label: "Private API Key", secret: true },
      { key: "site_id", label: "Site ID" },
    ],
  },
  {
    id: "mercadopago",
    featureId: "payment_mercadopago",
    label: "Mercado Pago",
    // qr:true = producto MP Orders QR; POS no lo usa hasta cablear qr_data (hoy preference = link).
    capabilities: { cards: true, qr: true, links: true, recurring: true, refunds: true },
    credentialFields: [
      { key: "access_token", label: "Access Token", secret: true },
      { key: "public_key", label: "Public Key" },
    ],
  },
  {
    id: "modo",
    featureId: "payment_modo",
    label: "MODO",
    capabilities: { cards: false, qr: true, links: true, recurring: false, refunds: false },
    credentialFields: [
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "store_id", label: "Store ID (comercio MODO)" },
      { key: "api_base_url", label: "API Base URL (opcional)" },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "getnet",
    featureId: "payment_getnet",
    label: "Getnet",
    capabilities: { cards: true, qr: false, links: true, recurring: false, refunds: true },
    credentialFields: [
      { key: "seller_id", label: "Seller ID" },
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "base_url", label: "Base URL API (opcional)" },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "uala",
    featureId: "payment_uala",
    label: "Ualá Bis",
    capabilities: { cards: true, qr: false, links: true, recurring: false, refunds: true },
    credentialFields: [
      { key: "username", label: "Username (Ualá)" },
      { key: "client_id", label: "Client ID" },
      { key: "client_secret_id", label: "Client Secret", secret: true },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "nave",
    featureId: "payment_nave",
    label: "Nave Negocios",
    capabilities: { cards: true, qr: true, links: true, recurring: false, refunds: false },
    credentialFields: [
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "pos_id", label: "ID Punto de Venta (POS ID)" },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "talo",
    featureId: "payment_talo",
    label: "Talo",
    // Cripto/Pix URL — no QR interoperable AR.
    capabilities: { cards: false, qr: false, links: true, recurring: false, refunds: false },
    credentialFields: [
      { key: "user_id", label: "User ID (Talo)" },
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "sipago",
    featureId: "payment_sipago",
    label: "Sipago",
    capabilities: { cards: true, qr: false, links: true, recurring: false, refunds: false },
    credentialFields: [
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
  {
    id: "dlocal",
    featureId: "payment_dlocal",
    label: "dLocal Go",
    capabilities: { cards: true, qr: false, links: true, recurring: false, refunds: true },
    credentialFields: [
      { key: "api_key", label: "API Key" },
      { key: "secret_key", label: "Secret Key", secret: true },
      { key: "environment", label: "Ambiente (sandbox o production)" },
    ],
  },
];

/** Solo billing SaaS — nunca exponer a tenants. */
export const INTERNAL_PAYMENT_PROVIDER_IDS: PaymentProviderId[] = [];

export function tenantPaymentProviders(): PaymentProviderDef[] {
  return PAYMENT_PROVIDERS.filter((p) => !INTERNAL_PAYMENT_PROVIDER_IDS.includes(p.id));
}

export function getProvider(id: string): PaymentProviderDef | undefined {
  return PAYMENT_PROVIDERS.find((p) => p.id === id);
}

/** Lifecycle efectivo del provider resuelto contra el catálogo (con overrides aplicados afuera). */
export function providerLifecycle(
  id: PaymentProviderId,
  overrides?: Record<string, { lifecycle?: FeatureLifecycle; enabled?: boolean }>,
): { lifecycle: FeatureLifecycle; enabled: boolean } {
  const def = getProvider(id);
  const feat = SM_FEATURES.find((f) => f.id === def?.featureId);
  const ov = def ? overrides?.[def.featureId] : undefined;
  return {
    lifecycle: ov?.lifecycle ?? feat?.lifecycle ?? "upcoming",
    enabled: ov?.enabled !== false,
  };
}

/** ¿Mostrar al tenant? Deprecado o flag global off → oculto en todos lados. */
export function isPaymentProviderTenantVisible(opts: {
  lifecycle: FeatureLifecycle | string;
  enabled?: boolean;
}): boolean {
  if (opts.enabled === false) return false;
  if (opts.lifecycle === "deprecated") return false;
  return true;
}

/** ¿Se puede configurar / cobrar? Solo stable|beta y visible. */
export function isPaymentProviderConfigurable(opts: {
  lifecycle: FeatureLifecycle | string;
  enabled?: boolean;
}): boolean {
  if (!isPaymentProviderTenantVisible(opts)) return false;
  return opts.lifecycle === "stable" || opts.lifecycle === "beta";
}
